import { Autocomplete, Checkbox, CircularProgress, InputLabel, TextField } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { searchScopeValues } from '../../../../services/scope.service';
import { assignValueToUserScope, fetchScopeValuesForUser, unassignValueFromUserScope } from '../../../../redux/slices/userScopeSlice';
import { useAppDispatch, useAppSelector } from '../../../../redux/hook';

interface UserScopeSelectorProps {
  appUserId: string;
  scopeName: string;
}

let timeout: ReturnType<typeof setTimeout>;

export const UserScopeSelector = (props: UserScopeSelectorProps) => {
  const dispatch = useAppDispatch();
  const userScopeState = useAppSelector((x) => x.userScopes);
  const { assignedScopeValues, isAssignedScopeValuesLoading } = userScopeState;
  const [options, setOptions] = useState<readonly ScopeValue[]>([]);
  const textFieldRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    dispatch(fetchScopeValuesForUser({ userId: props.appUserId, scopeName: props.scopeName }));
  }, [props.appUserId, props.scopeName, userScopeState.assignedTemplates]);

  const onValueChange = async (event: any, newValue: ScopeValue | null) => {
    if (newValue) {
      dispatch(assignValueToUserScope({ userId: props.appUserId, scopeName: props.scopeName, value: newValue.key }));
      if (textFieldRef.current) {
        textFieldRef.current.focus();
      }
    }
  };

  const onInputChange = (event: any, value: string) => {
    setTimerForAutocomplete(value);
  };

  const onCheckBoxChange = async (item: ScopeValue) => {
    dispatch(unassignValueFromUserScope({ userId: props.appUserId, scopeName: props.scopeName, value: item.key }));
  };

  const getSearchOptions = async (input: string) => {
    if (input.length < 1) {
      return;
    }

    await searchScopeValues(props.scopeName, input).then((response) => {
      if (!response?.data) {
        console.error('No results', response);
        return [];
      }
      setOptions(response.data);
    });
  };

  const setTimerForAutocomplete = (input: string) => {
    timeout && clearTimeout(timeout);
    timeout = setTimeout(() => getSearchOptions(input), 100);
  };

  return (
    <>
      <Autocomplete
        filterOptions={(x) => x} // disable default filtering, because we filter in the backend
        id='serialNumber'
        options={options}
        renderOption={(props, option) => (
          <span {...props} key={option.key}>
            {option.value}
          </span>
        )}
        getOptionLabel={(option) => option.value ?? ''}
        noOptionsText='No options'
        clearOnBlur={true}
        blurOnSelect={true}
        includeInputInList
        renderInput={(params) => <TextField variant='standard' {...params} label={'Search...'} inputRef={textFieldRef} />}
        onChange={onValueChange}
        onInputChange={onInputChange}
        value={null} // Reset value after selection
      />
      <div style={{ height: '1em', paddingTop: '0.5em', marginBottom: '0.5em', paddingLeft: '0.75em' }}>
        {isAssignedScopeValuesLoading && <CircularProgress style={{ height: '1em', width: '1em' }} />}
      </div>
      <div>
        {!Array.isArray(assignedScopeValues) || assignedScopeValues.length === 0 ? (
          <span className='txt-muted'>No values assigned</span>
        ) : (
          assignedScopeValues.map((x, idx) => (
            <InputLabel key={idx} style={{ width: '100%', cursor: x.isInherited ? 'default' : 'pointer' }}>
              <Checkbox checked disabled={x.isInherited} onChange={() => onCheckBoxChange(x)} />
              {x.value}
            </InputLabel>
          ))
        )}
      </div>
    </>
  );
};
